import clsx from "clsx";
import { api } from "../lib/api";
import { fmtRelative } from "../lib/format";
import type { MeAuthenticated } from "../lib/types";
import ThemeToggle from "./ThemeToggle";

interface HeaderProps {
  me: MeAuthenticated;
  theme: { theme: "light" | "dark"; toggle: () => void };
  lastSyncAt: string | null;
  loadingLastSync?: boolean;
  refreshing?: boolean;
  onRefresh: () => void;
  /** Connection status from /me. */
  connected: boolean;
}

export default function Header({
  me,
  theme,
  lastSyncAt,
  loadingLastSync,
  refreshing,
  onRefresh,
  connected,
}: HeaderProps) {
  async function onLogout() {
    await api.logout();
    window.location.href = "/";
  }

  return (
    <header className="sticky top-0 z-10 border-b border-zinc-200 bg-zinc-50/85 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/80">
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-3 sm:px-6">
        <Logo className="h-7 w-7 flex-none" />
        <div className="hidden flex-col sm:flex">
          <span className="text-sm font-semibold leading-none tracking-tight">
            Fitness RPG
          </span>
          <span className="mt-0.5 text-[11px] text-zinc-500">
            {me.user.email ?? me.user.name ?? "Strava athlete"}
          </span>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <SyncStatus
            connected={connected}
            lastSyncAt={lastSyncAt}
            loading={loadingLastSync}
          />
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="btn-outline h-9 px-3"
            aria-label="Refresh now"
            title="Refresh now"
          >
            <RefreshIcon
              className={clsx("h-4 w-4", refreshing && "animate-spin")}
            />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <ThemeToggle theme={theme} />
          <button
            type="button"
            onClick={onLogout}
            className="btn-ghost h-9 px-3 text-xs"
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

function SyncStatus({
  connected,
  lastSyncAt,
  loading,
}: {
  connected: boolean;
  lastSyncAt: string | null;
  loading?: boolean;
}) {
  let color = "bg-zinc-400";
  let label = "Idle";

  if (!connected) {
    color = "bg-amber-500";
    label = "Reconnect needed";
  } else if (loading) {
    color = "bg-amber-500 animate-pulse";
    label = "Syncing…";
  } else if (lastSyncAt) {
    color = "bg-emerald-500";
    label = `Synced ${fmtRelative(lastSyncAt)}`;
  } else {
    color = "bg-emerald-500";
    label = "Connected";
  }

  return (
    <div className="chip hidden bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 md:inline-flex">
      <span className={clsx("h-1.5 w-1.5 rounded-full", color)} />
      {label}
    </div>
  );
}

function Logo({ className }: { className?: string }) {
  return (
    <img
      src="/icons/strava-app-icon-124.png"
      alt=""
      className={className}
      aria-hidden="true"
    />
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}
